import React, { useEffect, useState, useRef } from "react";
import { Button, Spin, message } from "antd";
import { Base64 } from "js-base64";
import { getPayPalClientToken } from "../../api";
import { AmountDivideByHundred } from "../../helper";
import { H1Text, FlexContainer } from "../../design";
import SmallScreenView from "../smallscreen";
import { Hidden } from "react-awesome-styled-grid";
import { SpText } from "../../widgetDesign";
import GooglePayIcon from "../../assets/imgs/GOOGLE_PAY.svg";
import { API } from "../../api";

type Props = {
  headerText: string,
  payment: Object;
  chargeByGooglePay: Function;
  accountGid: String,
  setTransactionModalOpen: Function,
  splitUpPayload: Object,
};

const PAYPAL_SDK = "https://www.paypal.com/sdk/js";
const GOOGLE_PAY_SDK = "https://pay.google.com/gp/p/js/pay.js";

const PayPalGooglePay = (props: Props) => {
  const {
    headerText,
    payment,
    chargeByGooglePay,
    accountGid,
    setTransactionModalOpen,
    splitUpPayload
  } = props;

  const [loading, setLoading] = useState(true);
  const [eligible, setEligible] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState({});
  const splitUpStatus = useRef(false);

  const paymentsClientRef = useRef(null);
  const allowedPaymentMethodsRef = useRef(null);
  const merchantInfoRef = useRef(null);

  const getPaymentSplitUp = async () => {
    try {
      const { data : { entity } } = await API.post(`transaction-fee/split-up`, splitUpPayload);
      setPaymentInfo(entity);
      splitUpStatus.current = true;
    } catch (e) {
      console.error(e);
      splitUpStatus.current = false;
    }
  }

  const loadScript = (src, attrs = {}) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v));
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const onPaymentAuthorized = (paymentData) => {
    return new Promise(async (resolve) => {
      try {
        await chargeByGooglePay(paymentData);
        setTransactionModalOpen(false);
        resolve({ transactionState: "SUCCESS" });
      } catch (err) {
        setTransactionModalOpen(false);
        resolve({ transactionState: "ERROR" });
      }
    });
  };

  const getGooglePaymentsClient = () => {
    if (!paymentsClientRef.current) {
      paymentsClientRef.current = new window.google.payments.api.PaymentsClient({
        environment: process.env.REACT_APP_PAYPAL_GOOGLE_PAY_MODE || "TEST",
        paymentDataCallbacks: {
          onPaymentAuthorized,
        },
      });
    }
    return paymentsClientRef.current;
  };

  useEffect(() => {
    const init = async () => {
      try {
        await getPaymentSplitUp();
        const token = await getPayPalClientToken(accountGid);
        const decoded = JSON.parse(Base64.decode(token));

        const clientId = decoded.clientId;
        const merchantId = decoded.merchantId;

        await loadScript(
          `${PAYPAL_SDK}?components=googlepay&client-id=${clientId}&merchant-id=${merchantId}&currency=${payment?.account?.currency?.name}`,
          {
            "data-client-token": decoded?.token?.client_token,
          }
        );

        await loadScript(GOOGLE_PAY_SDK);

        const interval = setInterval(async () => {
          if (window.paypal && window.google?.payments?.api) {
            clearInterval(interval);

            const config = await window.paypal.Googlepay().config();
            allowedPaymentMethodsRef.current = config.allowedPaymentMethods;
            merchantInfoRef.current = config.merchantInfo;

            const client = getGooglePaymentsClient();

            const isReady = await client.isReadyToPay({
              apiVersion: config.apiVersion,
              apiVersionMinor: config.apiVersionMinor,
              allowedPaymentMethods: allowedPaymentMethodsRef.current,
            });

            setEligible(isReady.result && config.isEligible);
            setLoading(false);
          }
        }, 50);

      } catch (err) {
        console.error(err);
        setLoading(false);
        setEligible(false);
      }
    };

    init();
  }, []);

  const handleGooglePayClick = async () => {
    try {
      const allowedPaymentMethods = allowedPaymentMethodsRef.current;
      const merchantInfo = merchantInfoRef.current;
      if (!allowedPaymentMethods || !merchantInfo) {
        message.error("Google Pay not initialized");
        return;
      }

      let amountValue = AmountDivideByHundred(payment?.totalPayable ?? payment?.total ?? payment?.amount);
      if (splitUpStatus.current) {
        amountValue = AmountDivideByHundred(paymentInfo?.totalPayable);
      }

      if (!amountValue) {
        message.error("Invalid amount");
        return;
      }

      const client = getGooglePaymentsClient();
      const isReady = await client.isReadyToPay({
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods,
      });

      if (!isReady.result) {
        message.error("Google Pay not available");
        return;
      }

      setTransactionModalOpen(true);

      const paymentDataRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods,
        merchantInfo,
        transactionInfo: {
          totalPriceStatus: "FINAL",
          totalPrice: String(amountValue),
          currencyCode: payment?.account?.currency?.name,
          countryCode: payment?.account?.currency?.countryAlpha2,
        },
        emailRequired: true,
        callbackIntents: ["PAYMENT_AUTHORIZATION"],
      };

      await client.loadPaymentData(paymentDataRequest);

    } catch (err) {
      console.error("Google Pay Error:", err);
      setTransactionModalOpen(false);
      message.error(err?.message || "Google Pay Failed");
    }
  };

  if (loading) return <Spin spinning tip="Please wait..." />;
  if (!eligible) return <SpText>Not Eligible!</SpText>;

  return (
    <>
      <H1Text className="px-4 mb-0">
        <SmallScreenView>{"Pay With Google Pay"}</SmallScreenView>
        <Hidden sm xs>{headerText || "Pay With Google Pay"}</Hidden>
      </H1Text>

      <FlexContainer flexDirection="column">
        <div className="px-1 px-md-4">
          <div class="mt-4">
            <table class="table table-bordered mt-4">
              <tbody>
                {
                  paymentInfo?.amount ? (
                    <tr style={{ borderBottom: 'hidden' }}>
                      <th scope="row">Amount</th>
                      <td>{payment?.account?.currency?.prefix} {AmountDivideByHundred(paymentInfo?.amount)}</td>
                    </tr>
                  ) : (
                    <></>
                  )
                }
                {
                  paymentInfo?.coupleDiscountAmount ? (
                    <tr style={{ borderBottom: 'hidden' }}>
                      <th scope="row">Couple Discount</th>
                      <td>{payment?.account?.currency?.prefix} ({AmountDivideByHundred(paymentInfo?.coupleDiscountAmount)})</td>
                    </tr>
                  ) : (
                    <></>
                  )
                }
                {
                  paymentInfo?.deliveryFee ? (
                    <tr style={{ borderBottom: 'hidden' }}>
                      <th scope="row">Delivery Fee</th>
                      <td>{payment?.account?.currency?.prefix} {AmountDivideByHundred(paymentInfo?.deliveryFee)}</td>
                    </tr>
                  ) : (
                    <></>
                  )
                }
                {
                  paymentInfo?.discountedAmount ? (
                    <tr style={{ borderBottom: 'hidden' }}>
                      <th scope="row">Discount</th>
                      <td>{payment?.account?.currency?.prefix} ({AmountDivideByHundred(paymentInfo?.discountedAmount)})</td>
                    </tr>
                  ) : (
                    <></>
                  )
                }
                {
                  paymentInfo?.itemDiscount ? (
                    <tr style={{ borderBottom: 'hidden' }}>
                      <th scope="row">Item Discount</th>
                      <td>{payment?.account?.currency?.prefix} ({AmountDivideByHundred(paymentInfo?.itemDiscount)})</td>
                    </tr>
                  ) : (
                    <></>
                  )
                }
                {
                  paymentInfo?.net ? (
                    <tr style={{ borderBottom: 'hidden' }}>
                      <th scope="row">Net</th>
                      <td>{payment?.account?.currency?.prefix} {AmountDivideByHundred(paymentInfo?.net)}</td>
                    </tr>
                  ) : (
                    <></>
                  )
                }
                {
                  paymentInfo?.orderDiscount ? (
                    <tr style={{ borderBottom: 'hidden' }}>
                      <th scope="row">Order Discount</th>
                      <td>{payment?.account?.currency?.prefix} ({AmountDivideByHundred(paymentInfo?.orderDiscount)})</td>
                    </tr>
                  ) : (
                    <></>
                  )
                }
                {
                  paymentInfo?.serviceFee ? (
                    <tr style={{ borderBottom: 'hidden' }}>
                      <th scope="row">Service Fee</th>
                      <td>{payment?.account?.currency?.prefix} {AmountDivideByHundred(paymentInfo?.serviceFee)}</td>
                    </tr>
                  ) : (
                    <></>
                  )
                }
                {
                  paymentInfo?.serviceFeePercentage ? (
                    <tr style={{ borderBottom: 'hidden' }}>
                      <th scope="row">Service %</th>
                      <td>% {AmountDivideByHundred(paymentInfo?.serviceFeePercentage)}</td>
                    </tr>
                  ) : (
                    <></>
                  )
                }
                {
                  paymentInfo?.subTotal ? (
                    <tr style={{ borderBottom: 'hidden' }}>
                      <th scope="row">Sub Total</th>
                      <td>{payment?.account?.currency?.prefix} {AmountDivideByHundred(paymentInfo?.subTotal)}</td>
                    </tr>
                  ) : (
                    <></>
                  )
                }
                {
                  paymentInfo?.subTotalAfterDiscount ? (
                    <tr style={{ borderBottom: 'hidden' }}>
                      <th scope="row">Sub Total After Discount</th>
                      <td>{payment?.account?.currency?.prefix} {AmountDivideByHundred(paymentInfo?.subTotalAfterDiscount)}</td>
                    </tr>
                  ) : (
                    <></>
                  )
                }
                {
                  paymentInfo?.surcharge ? (
                    <tr style={{ borderBottom: 'hidden' }}>
                      <th scope="row">Surcharge</th>
                      <td>{payment?.account?.currency?.prefix} {AmountDivideByHundred(paymentInfo?.surcharge)}</td>
                    </tr>
                  ) : (
                    <></>
                  )
                }
                {
                  paymentInfo?.tax ? (
                    <tr style={{ borderBottom: 'hidden' }}>
                      <th scope="row">Tax</th>
                      <td>{payment?.account?.currency?.prefix} {AmountDivideByHundred(paymentInfo?.tax)}</td>
                    </tr>
                  ) : (
                    <></>
                  )
                }
                {
                  paymentInfo?.tip ? (
                    <tr style={{ borderBottom: 'hidden' }}>
                      <th scope="row">Tip</th>
                      <td>{payment?.account?.currency?.prefix} {AmountDivideByHundred(paymentInfo?.tip)}</td>
                    </tr>
                  ) : (
                    <></>
                  )
                }
                {
                  paymentInfo?.totalPayable ? (
                      <tr style={{ background: '#F7F7F7' }}>
                          <th scope="row">Total Payable</th>
                          <td class="font-weight-bold">{payment?.account?.currency?.prefix} {AmountDivideByHundred(paymentInfo?.totalPayable)}</td>
                      </tr>
                  ) : (
                      <></>
                  )
                }
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-4 mt-3">
          {
            paymentInfo?.surcharge ? (
              <div style={{ fontSize: "12px", color: "gray" }}>
                * {payment?.account?.currency?.prefix} {AmountDivideByHundred(paymentInfo?.surcharge)} fee applied (typically for credit cards)
              </div>
            ) : null
          }
        </div>
        <div className="px-4 mt-4 mb-4 w-100">
          <Button
            size="large"
            onClick={handleGooglePayClick}
            style={{
              background: "#000",
              color: "#fff",
              borderColor: "#000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <img src={GooglePayIcon} alt="Google Pay" />
            Pay with Google Pay
          </Button>
        </div>
      </FlexContainer>
    </>
  );
};

export default PayPalGooglePay;